import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, message, Modal, Select, Space } from 'antd';
import {
  ACTIVE_BUSINESS_MODULE_KEY,
  BUSINESS_MODULES,
  BusinessModuleDef,
  BusinessModuleKey,
  DEFAULT_MODULE_KEY,
  getModulePlaceholderPath,
} from '../../constants/businessModules';
import { useAuth } from '../../hooks/useAuth';
import { getMyModuleAccessApplications, submitModuleAccessApplication, validateWebsiteUrl } from '../../services/moduleAccessApplication';
import type { ModuleAccessApplication } from '../../services/moduleAccessApplication';

const getWarehouseDashboardPath = (role: 'admin' | 'staff' | 'customer' | undefined): string => {
  if (role === 'admin') return '/dashboard/admin';
  if (role === 'staff') return '/dashboard/staff';
  return '/dashboard/customer';
};

const normalizeEnabledModuleKeys = (enabledModules: string[] | undefined): Set<BusinessModuleKey> => {
  const labels = new Set((enabledModules || []).map((item) => String(item || '').trim()));
  const keys = new Set<BusinessModuleKey>();
  const labelAliasToKey: Record<string, BusinessModuleKey> = {
    '海外仓/仓配托管': 'warehouse_3pl',
    '包裹转运集运': 'transit_consolidation',
    '快递物流': 'courier_logistics',
    // 兼容历史/后端文案差异（有无斜杠）
    '商品采购分销代理': 'procurement_distribution',
    '商品采购/分销代理': 'procurement_distribution',
  };

  labels.forEach((label) => {
    const mapped = labelAliasToKey[label];
    if (mapped) keys.add(mapped);
  });

  BUSINESS_MODULES.forEach((item) => {
    if (labels.has(item.label)) {
      keys.add(item.key);
    }
  });
  return keys;
};

const SelectBusinessModulePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applyVisible, setApplyVisible] = useState(false);
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [selectedModule, setSelectedModule] = useState<BusinessModuleDef | null>(null);
  const [latestApplicationByModule, setLatestApplicationByModule] = useState<Record<string, ModuleAccessApplication>>({});
  const [applyForm] = Form.useForm();
  const [websiteCheckStatus, setWebsiteCheckStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [websiteCheckText, setWebsiteCheckText] = useState('');
  const [websiteNormalized, setWebsiteNormalized] = useState<string | undefined>(undefined);
  const websiteCheckSeqRef = useRef(0);
  const watchedWebsite = Form.useWatch('website', applyForm);

  const normalizeWebsiteLocal = (raw?: string): string | undefined => {
    const text = String(raw || '').trim();
    if (!text) return undefined;
    return /^https?:\/\//i.test(text) ? text : `http://${text}`;
  };

  const validateWebsiteLocally = (raw?: string): { ok: boolean; normalized?: string; detail?: string } => {
    const normalized = normalizeWebsiteLocal(raw);
    if (!normalized) return { ok: true, normalized: undefined };
    try {
      const parsed = new URL(normalized);
      if (!/^https?:$/i.test(parsed.protocol)) {
        return { ok: false, detail: '网站地址仅支持 http 或 https 协议' };
      }
      if (!parsed.hostname || parsed.hostname.includes(' ') || parsed.hostname.indexOf('.') < 0) {
        return { ok: false, detail: '网站地址格式不正确，请输入合法URL' };
      }
      return { ok: true, normalized };
    } catch {
      return { ok: false, detail: '网站地址格式不正确，请输入合法URL' };
    }
  };

  const isCustomerMain = user?.role === 'customer' && user?.customer_sub_role === 'main_account';
  const isCustomerSub = user?.role === 'customer' && user?.customer_sub_role === 'sub_account';

  React.useEffect(() => {
    if (!user) return;
    if (isCustomerSub) {
      localStorage.setItem(ACTIVE_BUSINESS_MODULE_KEY, DEFAULT_MODULE_KEY);
      navigate(getWarehouseDashboardPath(user.role), { replace: true });
    }
  }, [isCustomerSub, navigate, user]);

  const loadMyApplications = React.useCallback(async () => {
    if (!isCustomerMain) {
      setLatestApplicationByModule({});
      return;
    }
    try {
      const rows = await getMyModuleAccessApplications();
      const latestMap: Record<string, ModuleAccessApplication> = {};
      rows.forEach((item) => {
        latestMap[item.module_key] = item;
      });
      setLatestApplicationByModule(latestMap);
    } catch {
      setLatestApplicationByModule({});
    }
  }, [isCustomerMain]);

  React.useEffect(() => {
    loadMyApplications();
  }, [loadMyApplications]);

  React.useEffect(() => {
    const text = String(watchedWebsite || '').trim();
    if (!text) {
      setWebsiteCheckStatus('idle');
      setWebsiteCheckText('');
      setWebsiteNormalized(undefined);
      return;
    }
    const localCheck = validateWebsiteLocally(text);
    if (!localCheck.ok) {
      setWebsiteCheckStatus('invalid');
      setWebsiteCheckText(localCheck.detail || '网站地址格式不正确，请输入合法URL');
      setWebsiteNormalized(undefined);
      return;
    }
    setWebsiteCheckStatus('checking');
    setWebsiteCheckText('');
    setWebsiteNormalized(localCheck.normalized);
    const currentSeq = ++websiteCheckSeqRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const result = await validateWebsiteUrl(text);
        if (currentSeq !== websiteCheckSeqRef.current) return;
        if (result.valid) {
          setWebsiteCheckStatus('valid');
          setWebsiteCheckText('');
          setWebsiteNormalized(result.normalized || undefined);
        } else {
          setWebsiteCheckStatus('invalid');
          setWebsiteCheckText(result.detail || '网站地址格式不正确，请输入合法URL');
          setWebsiteNormalized(undefined);
        }
      } catch {
        if (currentSeq !== websiteCheckSeqRef.current) return;
        // 接口异常时保持静默，不给出不确定提示，最终以提交校验为准
        setWebsiteCheckStatus('idle');
        setWebsiteCheckText('');
        setWebsiteNormalized(localCheck.normalized);
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [watchedWebsite, applyForm]);

  const enabledForMain = useMemo(
    () => normalizeEnabledModuleKeys(user?.enabled_business_modules),
    [user?.enabled_business_modules]
  );

  const canEnterModule = (module: BusinessModuleDef): boolean => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'staff') return true;
    if (isCustomerMain) return enabledForMain.has(module.key);
    return module.key === DEFAULT_MODULE_KEY;
  };

  const enterModule = (module: BusinessModuleDef) => {
    if (!user) return;
    if (!canEnterModule(module)) return;
    localStorage.setItem(ACTIVE_BUSINESS_MODULE_KEY, module.key);
    if (module.key === DEFAULT_MODULE_KEY) {
      navigate(getWarehouseDashboardPath(user.role), { replace: true });
      return;
    }
    navigate(getModulePlaceholderPath(module.key), { replace: true });
  };

  const openApplyModal = (module: BusinessModuleDef) => {
    setSelectedModule(module);
    const latest = latestApplicationByModule[module.key];
    applyForm.resetFields();
    const socialAccounts = latest?.payload?.social_accounts || {};
    const socialList = Object.entries(socialAccounts).map(([platform, account]) => ({ platform, account }));
    const rawDescription = String(latest?.payload?.service_requirement_description || '')
      .replace(/^服务需求：.*(?:\r?\n)?/, '')
      .replace(/^补充说明：/, '')
      .trim();
    applyForm.setFieldsValue({
      company_name: latest?.payload?.company_name || undefined,
      website: latest?.payload?.website || undefined,
      personal_name: latest?.payload?.personal_name || undefined,
      main_product_categories: latest?.payload?.main_product_categories || [],
      sales_platform_links: latest?.payload?.sales_platform_links || [],
      phone_country_code: latest?.payload?.phone_country_code || undefined,
      phone_number: latest?.payload?.phone_number || undefined,
      social_accounts_list: socialList,
      service_requirement_items: latest?.payload?.service_requirement_items || [],
      service_requirement_description: rawDescription || undefined,
    });
    const websiteText = String(latest?.payload?.website || '').trim();
    if (!websiteText) {
      setWebsiteCheckStatus('idle');
      setWebsiteCheckText('');
      setWebsiteNormalized(undefined);
    }
    setApplyVisible(true);
  };

  const handleSubmitApply = async () => {
    if (!selectedModule) return;
    try {
      const values = await applyForm.validateFields();
      const websiteText = String(values.website || '').trim();
      let normalizedWebsite: string | undefined;
      if (websiteText) {
        const websiteResult = await validateWebsiteUrl(websiteText);
        if (!websiteResult.valid) {
          setWebsiteCheckStatus('invalid');
          setWebsiteCheckText(websiteResult.detail || '网站地址格式不正确，请输入合法URL');
          message.error(websiteResult.detail || '网站地址格式不正确，请输入合法URL');
          return;
        }
        normalizedWebsite = String(websiteResult.normalized || '').trim() || undefined;
      }
      setApplySubmitting(true);
      const socialList = Array.isArray(values.social_accounts_list) ? values.social_accounts_list : [];
      const socialDict = socialList.reduce((acc: Record<string, string>, cur: any) => {
        const platform = String(cur?.platform || '').trim();
        const account = String(cur?.account || '').trim();
        if (platform && account) acc[platform] = account;
        return acc;
      }, {});
      const payload = {
        company_name: values.company_name || undefined,
        website: normalizedWebsite || websiteNormalized || undefined,
        personal_name: values.personal_name || undefined,
        main_product_categories: (values.main_product_categories || [])
          .map((x: any) => String(x || '').trim())
          .filter((x: string) => !!x)
          .slice(0, 3),
        sales_platform_links: (values.sales_platform_links || [])
          .map((x: any) => String(x || '').trim())
          .filter((x: string) => !!x)
          .slice(0, 3),
        phone_country_code: values.phone_country_code || undefined,
        phone_number: values.phone_number || undefined,
        social_accounts: Object.keys(socialDict).length ? socialDict : undefined,
        service_requirement_items: Array.isArray(values.service_requirement_items)
          ? values.service_requirement_items.map((x: any) => String(x || '').trim()).filter((x: string) => !!x)
          : undefined,
        service_requirement_description: values.service_requirement_description || undefined,
      };
      await submitModuleAccessApplication(selectedModule.key, payload);
      message.success('申请已提交，等待管理员审核');
      setApplyVisible(false);
      await loadMyApplications();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.detail || '提交失败，请稍后重试');
    } finally {
      setApplySubmitting(false);
    }
  };

  if (!user || isCustomerSub) {
    return <div className="min-h-screen bg-gray-50" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-12">
      <div className="mx-auto max-w-5xl rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">请选择服务板块并进入管理后台</h1>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
          >
            返回首页
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {BUSINESS_MODULES.map((module) => {
            const enabled = canEnterModule(module);
            const latest = latestApplicationByModule[module.key];
            const status = latest?.status;
            const statusText = enabled
              ? '已开通权限'
              : status === 'pending'
                ? '申请审核中'
                : status === 'rejected'
                  ? '申请已驳回'
                  : status === 'approved'
                    ? '申请已通过'
                    : '未开通权限';
            return (
              <div key={module.key} className="rounded-xl border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900">{module.label}</h2>
                <div className="mt-5 flex items-center justify-between gap-3">
                  <span
                    className={`text-sm font-medium ${
                      enabled
                        ? 'text-green-600'
                        : status === 'rejected'
                          ? 'text-red-600'
                          : status === 'pending'
                            ? 'text-amber-700'
                            : 'text-gray-500'
                    }`}
                  >
                    {statusText}
                  </span>
                  {enabled ? (
                    <button
                      type="button"
                      onClick={() => enterModule(module)}
                      className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      进入后台
                    </button>
                  ) : isCustomerMain ? (
                    status === 'pending' ? (
                      <button
                        type="button"
                        disabled
                        className="cursor-not-allowed rounded border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700"
                      >
                        审核中
                      </button>
                    ) : status === 'rejected' ? (
                      <button
                        type="button"
                        onClick={() => openApplyModal(module)}
                        className="rounded border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                      >
                        重新申请
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openApplyModal(module)}
                        className="rounded border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                      >
                        申请开通权限
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="cursor-not-allowed rounded border border-gray-200 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-500"
                    >
                      申请开通权限
                    </button>
                  )}
                </div>
                {!enabled && status === 'rejected' && latest?.review_note ? (
                  <div className="mt-2 text-xs text-red-600">
                    驳回原因：{latest.review_note}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
      <Modal
        title={selectedModule ? `申请开通权限 - ${selectedModule.label}` : '申请开通权限'}
        open={applyVisible}
        onCancel={() => setApplyVisible(false)}
        onOk={handleSubmitApply}
        confirmLoading={applySubmitting}
        width={760}
        okText="提交申请"
        cancelText="取消"
      >
        <Form form={applyForm} layout="vertical">
          <Form.Item label="公司名称" name="company_name">
            <Input placeholder="公司中文或英文名称（可含数字）" allowClear />
          </Form.Item>
          <Form.Item
            label="公司网站"
            name="website"
            validateStatus={
              websiteCheckStatus === 'valid'
                ? 'success'
                : websiteCheckStatus === 'checking'
                  ? 'validating'
                  : websiteCheckStatus === 'invalid'
                    ? 'error'
                    : undefined
            }
            help={websiteCheckText || undefined}
          >
            <Input placeholder="https://example.com" allowClear />
          </Form.Item>
          <Form.Item label="个人姓名" name="personal_name">
            <Input placeholder="可选" allowClear maxLength={80} />
          </Form.Item>
          <Form.List name="main_product_categories">
            {(fields, { add, remove }) => (
              <Form.Item
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>主营产品类目</span>
                    <Button type="dashed" size="small" style={{ marginLeft: 30 }} onClick={() => add()} disabled={fields.length >= 3}>
                      添加类目
                    </Button>
                  </span>
                }
              >
                {fields.map((field, idx) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item {...field} rules={[{ required: true, message: '请输入类目' }]} style={{ minWidth: 360 }}>
                      <Input placeholder={`主营产品类目 ${idx + 1}`} allowClear />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>删除</Button>
                  </Space>
                ))}
              </Form.Item>
            )}
          </Form.List>
          <Form.List name="sales_platform_links">
            {(fields, { add, remove }) => (
              <Form.Item
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>销售平台店铺链接或产品链接</span>
                    <Button type="dashed" size="small" style={{ marginLeft: 30 }} onClick={() => add()} disabled={fields.length >= 3}>
                      添加链接
                    </Button>
                  </span>
                }
              >
                {fields.map((field, idx) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item {...field} rules={[{ required: true, message: '请输入链接' }]} style={{ minWidth: 360 }}>
                      <Input placeholder={`链接 ${idx + 1}`} allowClear />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>删除</Button>
                  </Space>
                ))}
              </Form.Item>
            )}
          </Form.List>
          <Form.Item label="电话国家码" name="phone_country_code">
            <Input placeholder="例如：+86 / +1" allowClear maxLength={8} />
          </Form.Item>
          <Form.Item label="电话号码" name="phone_number">
            <Input placeholder="不含国家码的电话号码" allowClear maxLength={30} />
          </Form.Item>
          <Form.List name="social_accounts_list">
            {(fields, { add, remove }) => (
              <Form.Item
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>社交媒体账号</span>
                    <Button type="dashed" size="small" style={{ marginLeft: 30 }} onClick={() => add()}>
                      添加社交媒体
                    </Button>
                  </span>
                }
              >
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'platform']}
                      fieldKey={[field.fieldKey!, 'platform']}
                      rules={[{ required: true, message: '请选择平台' }]}
                      style={{ minWidth: 160 }}
                    >
                      <Select
                        placeholder="选择平台"
                        options={[
                          { label: '微信', value: 'wechat' },
                          { label: 'QQ', value: 'qq' },
                          { label: 'WhatsApp', value: 'whatsapp' },
                          { label: 'Telegram', value: 'telegram' },
                          { label: 'Facebook', value: 'facebook' },
                          { label: 'LinkedIn', value: 'linkedin' },
                          { label: 'Twitter/X', value: 'twitter' },
                          { label: '抖音', value: 'douyin' },
                          { label: '小红书', value: 'xiaohongshu' },
                          { label: '其他', value: 'other' },
                        ]}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'account']}
                      fieldKey={[field.fieldKey!, 'account']}
                      rules={[{ required: true, message: '请输入账号或链接' }]}
                      style={{ minWidth: 280 }}
                    >
                      <Input placeholder="账号/链接" allowClear />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>删除</Button>
                  </Space>
                ))}
              </Form.Item>
            )}
          </Form.List>
          <Form.Item label="服务需求" name="service_requirement_items">
            <Select
              mode="multiple"
              allowClear
              placeholder="可多选"
              options={[
                { label: '一件代发', value: '一件代发' },
                { label: '贴换标', value: '贴换标' },
                { label: '备货中转', value: '备货中转' },
                { label: '退货处理', value: '退货处理' },
                { label: '其他', value: '其他' },
              ]}
            />
          </Form.Item>
          <Form.Item label="服务需求描述" name="service_requirement_description">
            <Input.TextArea placeholder="请补充服务需求说明（可选）" rows={4} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SelectBusinessModulePage;

